import type { MedplumClient } from '@medplum/core';
import type { ChargeItemDefinition, Extension, HealthcareService, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { getPractitionerOrganizationRef } from '@unify/fhir-model/resources/practitioner';
import {
  AI_PROMPT_KIND_NOTE_GENERATION,
  CALENDAR_EVENT_CATEGORY_SYSTEM,
  CPT_CODE_SYSTEM,
  EXEMPLAR_NOTE_QR_SELECTION_EXT,
  HCPCS_LEVEL_II_CODE_SYSTEM,
  HEALTHCARE_SERVICE_KEY_SYSTEM,
  INTAKE_ONLY_EXT,
  NOTE_TEMPLATE_ID_EXT,
  ONLINE_BOOKING_EXT,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM,
  PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE,
  SCHEDULING_PARAMETERS_EXT,
  SESSION_MODALITY_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  resolveAiPromptText,
  saveAiPromptText,
} from '@unify/fhir-model/ai-prompt-binary';
import {
  getChargeItemDefinitionRef,
  getExemplarNoteQrRefStrings,
  getHealthcareServiceCatalogKey,
  getIntakeOnly,
  getNoteTemplateId,
  getOnlineBooking,
  getSchedulingParametersDuration,
  getSessionModalityCode,
  isHealthcareServiceActive,
  markHealthcareServiceAsPlatformCatalog,
  setChargeItemDefinitionRef,
} from '@unify/fhir-model/resources/healthcare-service';
import {
  getBaseRateFromDefinition,
  getChargeItemDefinitionCode,
  getChargeItemDefinitionKey,
  resolveHealthcareServiceCatalogKey,
  upsertChargeItemDefinition,
} from '@unify/fhir-model/resources/charge-item-definition';
import {
  cascadeNewCatalogServiceToPractices,
  fetchProviderHealthcareServiceIds,
} from '@/lib/healthcareServiceEntitlement';
import { usePermissions } from '@/contexts/PermissionContext';
import type { Role } from '@/lib/rbacRoles';

export type AppointmentSessionModality = 'in-person' | 'telehealth' | 'hybrid' | 'either';

export interface AppointmentSessionType {
  id: string;
  description: string;
  durationMinutes: number;
  rate: number;
  cptCode: string;
  chargeItemDefinitionId?: string;
  billingTitle?: string;
  codeSystem?: string;
  onlineBooking: boolean;
  active: boolean;
  modality: AppointmentSessionModality;
  noteTemplateId: string | null;
  intakeOnly?: boolean;
  /** Per-service-type AI note generation system prompt. Absent means use the default. */
  aiNoteGenerationPrompt?: string;
  /** IDs of selected exemplar QuestionnaireResponse resources for this service type. */
  exemplarNoteQrIds: string[];
}

/**
 * Default AI note generation system prompt used when a service type has no custom prompt.
 * Must stay in sync with DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT in medplum-server/src/bots/ai-note-helpers.ts.
 */
export const DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT = [
  'You are a fully licensed clinical therapist generating therapy progress notes from session transcripts.',
  'Output must be valid JSON with shape: { "item": QuestionnaireResponseItem[] }.',
  'Each item must use linkId values exactly from the provided template schema.',
  'Follow the template structure exactly — populate every field in the schema, including all choice and multi-choice items.',
  'Include ONLY information present in the transcript, session metadata, sidebar notes, or existing in-progress answers.',
  'Do NOT hallucinate or fabricate clinical content not supported by those sources.',
  'Do not include client or third-party personal names — use "the client" instead.',
  'Generation rules:',
  'Write as a fully licensed therapist with extensive clinical documentation experience.',
  'Be succinct. For free-text fields, use about three to five complete sentences unless the template requires less.',
  'Strictly do not use adverbs in the clinical narrative (e.g., significantly, effectively, quickly, really, very). Use precise verbs and nouns instead.',
  'Do not use direct quotes under any circumstances.',
  'Focus on the main clinical topics in the transcript; de-emphasize or omit small talk or settling-in at the beginning unless clinically relevant.',
  'Interventions utilized: include only the top one to four interventions actually used this session (from template answer options).',
  'Client response to interventions: describe how the client responded to each intervention you listed and what they learned or practiced.',
  'Fill out every template field, including all choice and multi-choice items. Use supported answers from the template schema.',
  'For choice fields, use exact option text or coding values from the schema. Use None / N/A only when clearly supported by the transcript or session context.',
  'Strict fidelity: do not invent symptoms, interventions, risk factors, medications, homework, or events not explicitly in the transcript or reasonably derived from the conversation.',
  'Do not fabricate clinical observations, progress, or plans. If information is insufficient for a field, state only what is supported rather than guessing.',
  'Documentation quality standards:',
  "Paraphrase client language; do not use direct quoted speech (no straight or curly quotes around client words).",
  'Do not include client or third-party personal names in the clinical narrative — use "the client" instead.',
  'Write complete sentences; avoid trailing fragments or incomplete thoughts.',
  'Use clinically appropriate detail on sensitive topics; avoid unnecessary specifics unless required for care or safety.',
  "Reflect today's unique session content rather than copying prior note wording.",
].join('\n');
export interface MeetingType {
  id: string;
  description: string;
  /** Default block length when scheduling this meeting type (minutes). */
  defaultDurationMinutes: number;
  active: boolean;
}
export interface TimeEntryType {
  id: string;
  description: string;
  /** Default block length for this time entry type (minutes). */
  defaultDurationMinutes: number;
  active: boolean;
}

export interface CatalogChargeItemDefinition {
  id: string;
  catalogKey: string;
  title: string;
  code: string;
  codeSystem?: string;
  rate: number;
  active: boolean;
}
export const SESSION_DURATION_PRESETS = [5, 10, 15, 20, 23, 30, 38, 40, 45, 50, 53, 60, 90] as const;

const CPT_SYSTEM = CPT_CODE_SYSTEM;

const MODALITY_DISPLAY: Record<AppointmentSessionModality, string> = {
  'in-person': 'In-Person',
  telehealth: 'Telehealth',
  hybrid: 'Hybrid',
  either: 'In-Person or Telehealth',
};

const VALID_MODALITIES = new Set<AppointmentSessionModality>([
  'in-person',
  'telehealth',
  'hybrid',
  'either',
]);

function categoryCoding(code: string, display: string) {
  return {
    coding: [
      {
        system: CALENDAR_EVENT_CATEGORY_SYSTEM,
        code,
        display,
      },
    ],
  };
}

function schedulingDurationExtension(minutes: number): Extension {
  return {
    url: SCHEDULING_PARAMETERS_EXT,
    extension: [
      {
        url: 'duration',
        valueDuration: {
          value: minutes,
          unit: 'min',
          system: 'http://unitsofmeasure.org',
          code: 'min',
        },
      },
    ],
  };
}

function parseModality(code: string | undefined): AppointmentSessionModality {
  if (code && VALID_MODALITIES.has(code as AppointmentSessionModality)) {
    return code as AppointmentSessionModality;
  }
  return 'either';
}

export function hsToSessionType(
  hs: HealthcareService,
  opts?: { definition?: ChargeItemDefinition },
): AppointmentSessionType {
  const id = hs.id ?? '';
  const rawExemplarRefs = getExemplarNoteQrRefStrings(hs);
  const definition = opts?.definition;
  const billingCoding = definition?.code?.coding?.find((coding) => coding.code?.trim());
  return {
    id,
    description: hs.name ?? '',
    durationMinutes: getSchedulingParametersDuration(hs) ?? 60,
    rate: definition ? getBaseRateFromDefinition(definition) ?? 0 : 0,
    cptCode: billingCoding?.code ?? '',
    chargeItemDefinitionId: definition?.id,
    billingTitle: definition?.title ?? definition?.code?.coding?.[0]?.display,
    codeSystem: billingCoding?.system,
    onlineBooking: getOnlineBooking(hs),
    active: isHealthcareServiceActive(hs),
    modality: parseModality(getSessionModalityCode(hs)),
    noteTemplateId: getNoteTemplateId(hs),
    intakeOnly: getIntakeOnly(hs) || undefined,
    // Loaded asynchronously from tagged Binary — see useAppointmentSessionTypes / upsert.
    aiNoteGenerationPrompt: undefined,
    exemplarNoteQrIds: rawExemplarRefs.map((ref) => ref.replace('QuestionnaireResponse/', '')),
  };
}

export function sessionTypeToHs(sessionType: AppointmentSessionType): HealthcareService {
  const extensions: Extension[] = [
    schedulingDurationExtension(sessionType.durationMinutes),
    { url: ONLINE_BOOKING_EXT, valueBoolean: sessionType.onlineBooking },
  ];
  if (sessionType.noteTemplateId) {
    extensions.push({ url: NOTE_TEMPLATE_ID_EXT, valueString: sessionType.noteTemplateId });
  }
  if (sessionType.intakeOnly) {
    extensions.push({ url: INTAKE_ONLY_EXT, valueBoolean: true });
  }
  for (const qrId of sessionType.exemplarNoteQrIds ?? []) {
    if (qrId?.trim()) {
      extensions.push({
        url: EXEMPLAR_NOTE_QR_SELECTION_EXT,
        valueReference: { reference: `QuestionnaireResponse/${qrId.trim()}` },
      });
    }
  }

  const hs: HealthcareService = {
    resourceType: 'HealthcareService',
    id: sessionType.id,
    active: sessionType.active,
    category: [categoryCoding('clinical-session', 'Clinical Session')],
    name: sessionType.description,
    // Session-type-group coding retired (ENG-3443) — do not write session-type-group CodeSystem.
    appointmentRequired: true,
    serviceProvisionCode: [
      {
        coding: [
          {
            system: SESSION_MODALITY_SYSTEM,
            code: sessionType.modality,
            display: MODALITY_DISPLAY[sessionType.modality],
          },
        ],
      },
    ],
    extension: extensions,
  };

  return markHealthcareServiceAsPlatformCatalog(hs);
}

export function hsToMeetingType(hs: HealthcareService): MeetingType {
  return {
    id: hs.id ?? '',
    description: hs.name ?? '',
    defaultDurationMinutes: getSchedulingParametersDuration(hs) ?? 60,
    active: isHealthcareServiceActive(hs),
  };
}

export function meetingTypeToHs(meetingType: MeetingType): HealthcareService {
  const hs: HealthcareService = {
    resourceType: 'HealthcareService',
    id: meetingType.id,
    active: meetingType.active,
    category: [categoryCoding('meeting', 'Meeting')],
    name: meetingType.description,
    appointmentRequired: true,
    extension: [schedulingDurationExtension(meetingType.defaultDurationMinutes)],
  };
  return markHealthcareServiceAsPlatformCatalog(hs);
}

export function hsToTimeEntryType(hs: HealthcareService): TimeEntryType {
  return {
    id: hs.id ?? '',
    description: hs.name ?? '',
    defaultDurationMinutes: getSchedulingParametersDuration(hs) ?? 60,
    active: isHealthcareServiceActive(hs),
  };
}

export function timeEntryTypeToHs(timeEntryType: TimeEntryType): HealthcareService {
  const hs: HealthcareService = {
    resourceType: 'HealthcareService',
    id: timeEntryType.id,
    active: timeEntryType.active,
    category: [categoryCoding('time-entry', 'Time Entry')],
    name: timeEntryType.description,
    appointmentRequired: true,
    extension: [schedulingDurationExtension(timeEntryType.defaultDurationMinutes)],
  };
  return markHealthcareServiceAsPlatformCatalog(hs);
}

// ---------------------------------------------------------------------------
// Medplum-backed catalog store
// ---------------------------------------------------------------------------

let catalogVersion = 0;
const catalogListeners = new Set<() => void>();



function getCatalogVersion(): number {
  return catalogVersion;
}

function subscribeCatalog(listener: () => void): () => void {
  catalogListeners.add(listener);
  return () => catalogListeners.delete(listener);
}

export function emitCatalogChange(): void {
  catalogVersion += 1;
  catalogListeners.forEach((listener) => listener());
}

export function definitionToCatalogChargeItem(
  def: ChargeItemDefinition,
): CatalogChargeItemDefinition | undefined {
  if (!def.id) return undefined;
  const code = getChargeItemDefinitionCode(def);
  if (!code) return undefined;
  return {
    id: def.id,
    catalogKey: getChargeItemDefinitionKey(def) ?? def.id,
    title: def.title?.trim() || def.code?.coding?.[0]?.display?.trim() || code,
    code,
    codeSystem: def.code?.coding?.find((coding) => coding.code === code)?.system,
    rate: getBaseRateFromDefinition(def) ?? 0,
    active: def.status !== 'retired' && def.status !== 'unknown',
  };
}

export function allocateChargeItemDefinitionCatalogKey(
  code: string,
  existingKeys: Iterable<string>,
): string {
  const slug = code.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const base = `clinical-${slug || 'code'}`;
  const used = new Set(existingKeys);
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}

export function billingCodeSystemForCode(code: string): string {
  return /^[A-Za-z]/.test(code.trim()) ? HCPCS_LEVEL_II_CODE_SYSTEM : CPT_CODE_SYSTEM;
}

function useCatalogVersion(): number {
  return useSyncExternalStore(subscribeCatalog, getCatalogVersion, getCatalogVersion);
}

function buildHealthcareServiceSearch(
  categoryCode: 'clinical-session' | 'meeting' | 'time-entry',
): Record<string, string> {
  return {
    'service-category': `${CALENDAR_EVENT_CATEGORY_SYSTEM}|${categoryCode}`,
    // Restrict to platform-catalog entries only. Non-platform HealthcareService resources
    // would be accepted in the UI but rejected by the scheduling bot, causing confusing failures.
    identifier: `${PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM}|${PLATFORM_HEALTHCARE_SERVICE_IDENTIFIER_VALUE}`,
    active: 'true',
    _count: '100',
  };
}

export function buildClinicalSessionRatesByCatalogKey(
  definitions: import('@medplum/fhirtypes').ChargeItemDefinition[],
): Map<string, number> {
  const rateByCatalogKey = new Map<string, number>();
  for (const def of definitions) {
    const key = getChargeItemDefinitionKey(def);
    const rate = getBaseRateFromDefinition(def);
    if (key && rate != null) rateByCatalogKey.set(key, rate);
  }
  return rateByCatalogKey;
}

export function resolveClinicalSessionDefinition(
  hs: HealthcareService,
  definitionsById: ReadonlyMap<string, ChargeItemDefinition>,
  definitionsByCatalogKey: ReadonlyMap<string, ChargeItemDefinition>,
): ChargeItemDefinition | undefined {
  const refId = getChargeItemDefinitionRef(hs)?.reference?.match(/^ChargeItemDefinition\/([^/]+)$/)?.[1];
  if (refId) {
    const byRef = definitionsById.get(refId);
    if (byRef) return byRef;
  }
  const catalogKey = getHealthcareServiceCatalogKey(hs);
  return catalogKey ? definitionsByCatalogKey.get(catalogKey) : undefined;
}

async function ensureHealthcareServiceCatalogKey(
  medplum: MedplumClient,
  hs: HealthcareService,
): Promise<HealthcareService> {
  if (getHealthcareServiceCatalogKey(hs)) return hs;
  const derivedKey = resolveHealthcareServiceCatalogKey(hs);
  if (!derivedKey || !hs.id) return hs;
  return medplum.updateResource({
    ...hs,
    identifier: [
      ...(hs.identifier ?? []).filter((id) => id.system !== HEALTHCARE_SERVICE_KEY_SYSTEM),
      { system: HEALTHCARE_SERVICE_KEY_SYSTEM, value: derivedKey },
    ],
  });
}

/**
 * Whether the session-type catalog reads ChargeItemDefinitions (CPT code, billing title, rate).
 * Unify Admin has no ChargeItemDefinition access (P9-H: billing is out of MVP scope for admins),
 * so its session types load without billing fields.
 */
export function sessionTypeCatalogReadsBilling(role: Role): boolean {
  return role !== 'unify-admin';
}

export function useAppointmentSessionTypes(): AppointmentSessionType[] {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const { ready, activeRole } = usePermissions();
  const readsBilling = sessionTypeCatalogReadsBilling(activeRole);
  const [types, setTypes] = useState<AppointmentSessionType[]>([]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    medplum.invalidateSearches('HealthcareService');
    medplum.invalidateSearches('ChargeItemDefinition');
    Promise.all([
      medplum.searchResources('HealthcareService', buildHealthcareServiceSearch('clinical-session')),
      readsBilling
        ? medplum.searchResources('ChargeItemDefinition', {
          status: 'active',
          _count: '200',
        })
        : Promise.resolve([] as ChargeItemDefinition[]),
    ])
      .then(async ([resources, definitions]) => {
        if (cancelled) return;
        const definitionsById = new Map(
          definitions.flatMap((definition) => definition.id ? [[definition.id, definition] as const] : []),
        );
        const definitionsByCatalogKey = new Map(
          definitions.flatMap((definition) => {
            const key = getChargeItemDefinitionKey(definition);
            return key ? [[key, definition] as const] : [];
          }),
        );
        const mapped = await Promise.all(
          resources.map(async (hs) => {
            const definition = resolveClinicalSessionDefinition(
              hs,
              definitionsById,
              definitionsByCatalogKey,
            );
            const row = hsToSessionType(hs, { definition });
            if (row.id) {
              row.aiNoteGenerationPrompt = await resolveAiPromptText(
                medplum,
                AI_PROMPT_KIND_NOTE_GENERATION,
                `HealthcareService/${row.id}`,
              );
            }
            return row;
          }),
        );
        if (cancelled) return;
        setTypes(mapped.filter((row) => row.id));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load clinical session types', err);
          setTypes([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, version, ready, readsBilling]);

  return types;
}

/** Session types excluding intake-only entries — for provider calendar dropdowns. */
export function useSchedulableSessionTypes(): AppointmentSessionType[] {
  const all = useAppointmentSessionTypes();
  return all.filter((t) => t.active && !t.intakeOnly);
}

type ProviderSessionRow = AppointmentSessionType & {
  enabledForProvider: boolean;
};

/**
 * Viewer scope for the session-type dropdown. Providers stay on their
 * `PractitionerRole.healthcareService` allowlist; practice staff and Unify staff
 * see the full platform catalog. The UI does not read the practice-enabled List
 * (practice staff have no List access); the eligibility bot still enforces it on save.
 */
export type CalendarSessionTypeScope = 'provider' | 'practice' | 'platform';

/**
 * Pure resolver for calendar dropdowns. Mirrors `assertHealthcareServiceAllowedForProvider`
 * for providers: offering a wider set than the provider allowlist would fail on save.
 */
export function resolveCalendarSessionTypes(params: {
  providerRows: ProviderSessionRow[];
  scope?: CalendarSessionTypeScope;
}): AppointmentSessionType[] {
  const scope = params.scope ?? 'provider';
  const schedulable = params.providerRows.filter((t) => !t.intakeOnly);
  if (scope !== 'provider') return schedulable;
  return schedulable.filter((t) => t.enabledForProvider);
}

/** Resolve practice org id from profile extension, falling back to PractitionerRole.organization. */
export function useCalendarPracticeId(practitioner: Practitioner | undefined): string {
  const medplum = useMedplum();
  const fromExtension = useMemo(() => {
    if (!practitioner) return '';
    const ref = getPractitionerOrganizationRef(practitioner);
    return ref?.reference?.replace('Organization/', '') ?? '';
  }, [practitioner]);
  const [fromRole, setFromRole] = useState('');

  useEffect(() => {
    if (fromExtension || !practitioner?.id) {
      setFromRole('');
      return;
    }
    let cancelled = false;
    medplum.invalidateSearches('PractitionerRole');
    medplum
      .searchResources('PractitionerRole', {
        practitioner: `Practitioner/${practitioner.id}`,
        _count: '10',
      })
      .then((roles) => {
        if (cancelled) return;
        const role = roles.find((r) => r.active !== false) ?? roles[0];
        setFromRole(role?.organization?.reference?.replace('Organization/', '') ?? '');
      })
      .catch(() => {
        if (!cancelled) setFromRole('');
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, practitioner?.id, fromExtension]);

  return fromExtension || fromRole;
}

/** Effective schedulable session types for the provider calendar dropdown. */
export function useProviderSchedulableSessionTypes(
  practiceId: string,
  practitionerId: string,
  scope: CalendarSessionTypeScope = 'provider',
): AppointmentSessionType[] {
  const rows = useProviderSessionTypeEnablement(practiceId, practitionerId);
  return resolveCalendarSessionTypes({ providerRows: rows, scope });
}

export function useMeetingTypes(): MeetingType[] {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const [types, setTypes] = useState<MeetingType[]>([]);

  useEffect(() => {
    let cancelled = false;
    medplum.invalidateSearches('HealthcareService');
    medplum
      .searchResources('HealthcareService', buildHealthcareServiceSearch('meeting'))
      .then((resources) => {
        if (cancelled) return;
        const mapped = resources.map(hsToMeetingType).filter((row) => row.id);
        setTypes(mapped);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load meeting types', err);
          setTypes([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, version]);

  return types;
}

/** Effective meeting types for a provider. */
export function useProviderMeetingTypes(practiceId: string, practitionerId: string): MeetingType[] {
  const rows = useProviderMeetingTypeEnablement(practiceId, practitionerId);
  return rows.filter((t) => t.enabledForProvider);
}

export function useTimeEntryTypes(): TimeEntryType[] {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const [types, setTypes] = useState<TimeEntryType[]>([]);

  useEffect(() => {
    let cancelled = false;
    medplum.invalidateSearches('HealthcareService');
    medplum
      .searchResources('HealthcareService', buildHealthcareServiceSearch('time-entry'))
      .then((resources) => {
        if (cancelled) return;
        const mapped = resources.map(hsToTimeEntryType).filter((row) => row.id);
        setTypes(mapped);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load time entry types', err);
          setTypes([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, version]);

  return types;
}

export function useChargeItemDefinitions(): CatalogChargeItemDefinition[] {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const [rows, setRows] = useState<CatalogChargeItemDefinition[]>([]);

  useEffect(() => {
    let cancelled = false;
    medplum.invalidateSearches('ChargeItemDefinition');
    medplum
      .searchResources('ChargeItemDefinition', { _count: '200' })
      .then((resources) => {
        if (cancelled) return;
        setRows(resources.flatMap((def) => {
          const row = definitionToCatalogChargeItem(def);
          return row ? [row] : [];
        }));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load charge item definitions', err);
          setRows([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, version]);

  return rows;
}

/** Effective time entry types for a provider. */
export function useProviderTimeEntryTypes(practiceId: string, practitionerId: string): TimeEntryType[] {
  const rows = useProviderTimeEntryTypeEnablement(practiceId, practitionerId);
  return rows.filter((t) => t.enabledForProvider);
}

function sanitizeSessionType(row: AppointmentSessionType): AppointmentSessionType {
  return {
    ...row,
    description: row.description.trim(),
    durationMinutes: Math.max(1, Math.round(row.durationMinutes)),
    cptCode: row.cptCode.trim().toUpperCase(),
    billingTitle: row.billingTitle?.trim() || undefined,
    codeSystem: row.codeSystem?.trim() || CPT_SYSTEM,
  };
}

function newSessionTypeCatalogKey(row: AppointmentSessionType): string {
  const source = row.cptCode || row.description;
  const slug = source
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `clinical-${slug || Date.now()}`;
}

export async function upsertAppointmentSessionType(
  medplum: MedplumClient,
  sessionType: AppointmentSessionType,
  options?: { skipAiPrompt?: boolean },
): Promise<void> {
  const sanitized = sanitizeSessionType(sessionType);
  let savedHs: HealthcareService;
  let catalogKey: string;
  if (sanitized.id) {
    const existing = await medplum.readResource('HealthcareService', sanitized.id);
    catalogKey = getHealthcareServiceCatalogKey(existing) ?? newSessionTypeCatalogKey(sanitized);
    const built = sessionTypeToHs(sanitized);
    const identifiers = [...(built.identifier ?? [])];
    if (!identifiers.some((id) => id.system === HEALTHCARE_SERVICE_KEY_SYSTEM)) {
      identifiers.push({ system: HEALTHCARE_SERVICE_KEY_SYSTEM, value: catalogKey });
    }
    savedHs = await medplum.updateResource({ ...existing, ...built, identifier: identifiers });
  } else {
    const hs = sessionTypeToHs(sanitized);
    catalogKey = newSessionTypeCatalogKey(sanitized);
    hs.identifier = [
      ...(hs.identifier ?? []),
      { system: HEALTHCARE_SERVICE_KEY_SYSTEM, value: catalogKey },
    ];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, ...hsWithoutId } = hs;
    savedHs = await medplum.createResource(hsWithoutId);
    if (savedHs.id) {
      await cascadeNewCatalogServiceToPractices(medplum, savedHs.id);
    }
  }
  savedHs = await ensureHealthcareServiceCatalogKey(medplum, savedHs);
  if (sanitized.chargeItemDefinitionId) {
    const definition = await medplum.readResource(
      'ChargeItemDefinition',
      sanitized.chargeItemDefinitionId,
    );
    if (savedHs.id && definition.id) {
      savedHs = await medplum.updateResource(
        setChargeItemDefinitionRef(savedHs, {
          reference: `ChargeItemDefinition/${definition.id}`,
          display: definition.title,
        }),
      );
    }
  } else if (sanitized.cptCode) {
    const definition = await upsertChargeItemDefinition(medplum, catalogKey, {
      code: sanitized.cptCode,
      codeSystem: sanitized.codeSystem ?? CPT_SYSTEM,
      title: sanitized.billingTitle ?? sanitized.description,
      rate: sanitized.rate,
    });
    if (savedHs.id && definition.id) {
      savedHs = await medplum.updateResource(
        setChargeItemDefinitionRef(savedHs, {
          reference: `ChargeItemDefinition/${definition.id}`,
          display: definition.title,
        }),
      );
    }
  } else if (savedHs.id && getChargeItemDefinitionRef(savedHs)) {
    savedHs = await medplum.updateResource(setChargeItemDefinitionRef(savedHs, undefined));
  }
  if (savedHs.id && !options?.skipAiPrompt) {
    const trimmedPrompt = sanitized.aiNoteGenerationPrompt?.trim();
    const toSave =
      !trimmedPrompt || trimmedPrompt === DEFAULT_AI_NOTE_GENERATION_SYSTEM_PROMPT.trim()
        ? undefined
        : trimmedPrompt;
    await saveAiPromptText(
      medplum,
      AI_PROMPT_KIND_NOTE_GENERATION,
      `HealthcareService/${savedHs.id}`,
      toSave,
    );
  }
  medplum.invalidateSearches('HealthcareService');
  medplum.invalidateSearches('ChargeItemDefinition');
  medplum.invalidateSearches('DocumentReference');
  medplum.invalidateSearches('Binary');
  emitCatalogChange();
}

export async function upsertChargeItemDefinitionRow(
  medplum: MedplumClient,
  row: CatalogChargeItemDefinition,
): Promise<void> {
  const code = row.code.trim().toUpperCase();
  const title = row.title.trim();
  if (!code || !title) return;

  let catalogKey = row.catalogKey.trim();
  if (!row.id || !catalogKey) {
    const existing = await medplum.searchResources('ChargeItemDefinition', { _count: '200' });
    const existingKeys = existing.flatMap((def) => {
      const key = getChargeItemDefinitionKey(def);
      return key ? [key] : [];
    });
    catalogKey = allocateChargeItemDefinitionCatalogKey(code, existingKeys);
  }

  await upsertChargeItemDefinition(medplum, catalogKey, {
    code,
    codeSystem: row.codeSystem?.trim() || billingCodeSystemForCode(code),
    title,
    rate: Math.max(0, row.rate),
    status: row.active ? 'active' : 'retired',
  });
  medplum.invalidateSearches('ChargeItemDefinition');
  emitCatalogChange();
}

function sanitizeMeetingType(row: MeetingType): MeetingType {
  return {
    ...row,
    description: row.description.trim(),
    defaultDurationMinutes: Math.max(1, Math.round(row.defaultDurationMinutes)),
  };
}

function sanitizeTimeEntryType(row: TimeEntryType): TimeEntryType {
  return {
    ...row,
    description: row.description.trim(),
    defaultDurationMinutes: Math.max(1, Math.round(row.defaultDurationMinutes)),
  };
}

export function makeMeetingTypeId(description: string): string {
  const slug = description
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `meeting-${slug}` : `meeting-${Date.now()}`;
}

export function makeTimeEntryTypeId(description: string): string {
  const slug = description
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `time-${slug}` : `time-${Date.now()}`;
}

export async function upsertMeetingType(
  medplum: MedplumClient,
  meetingType: MeetingType,
): Promise<void> {
  const sanitized = sanitizeMeetingType(meetingType);
  if (sanitized.id) {
    const existing = await medplum.readResource('HealthcareService', sanitized.id);
    await medplum.updateResource({ ...existing, ...meetingTypeToHs(sanitized) });
  } else {
    const hs = meetingTypeToHs(sanitized);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, ...hsWithoutId } = hs;
    await medplum.createResource(hsWithoutId);
  }
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
}

export async function upsertTimeEntryType(
  medplum: MedplumClient,
  timeEntryType: TimeEntryType,
): Promise<void> {
  const sanitized = sanitizeTimeEntryType(timeEntryType);
  if (sanitized.id) {
    const existing = await medplum.readResource('HealthcareService', sanitized.id);
    await medplum.updateResource({ ...existing, ...timeEntryTypeToHs(sanitized) });
  } else {
    const hs = timeEntryTypeToHs(sanitized);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, ...hsWithoutId } = hs;
    await medplum.createResource(hsWithoutId);
  }
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
}

export async function removeMeetingType(medplum: MedplumClient, id: string): Promise<void> {
  const existing = await medplum.readResource('HealthcareService', id);
  await medplum.updateResource({ ...existing, active: false });
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
}

export async function removeTimeEntryType(medplum: MedplumClient, id: string): Promise<void> {
  const existing = await medplum.readResource('HealthcareService', id);
  await medplum.updateResource({ ...existing, active: false });
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
}

export async function removeAppointmentSessionType(
  medplum: MedplumClient,
  sessionTypeId: string,
): Promise<void> {
  const existing = await medplum.readResource('HealthcareService', sessionTypeId);
  await medplum.updateResource({ ...existing, active: false });
  medplum.invalidateSearches('HealthcareService');
  emitCatalogChange();
}

export function makeSessionTypeId(description: string): string {
  const slug = description
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `session-${Date.now()}`;
}

// ---------------------------------------------------------------------------
// Provider-level allowlist (PractitionerRole.healthcareService)
// ---------------------------------------------------------------------------

function useProviderAllowlistIdSet(practiceId: string, practitionerId: string): Set<string> {
  const medplum = useMedplum();
  const version = useCatalogVersion();
  const [allowlistIds, setAllowlistIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!practiceId || !practitionerId) {
      setAllowlistIds(new Set());
      return;
    }
    let cancelled = false;
    medplum.invalidateSearches('PractitionerRole');
    fetchProviderHealthcareServiceIds(medplum, practitionerId, practiceId)
      .then((ids) => {
        if (!cancelled) setAllowlistIds(new Set(ids));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[catalog] failed to load provider healthcare services', err);
          setAllowlistIds(new Set());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, practiceId, practitionerId, version]);

  return allowlistIds;
}

export function useProviderSessionTypeEnablement(
  practiceId: string,
  practitionerId: string,
): ProviderSessionRow[] {
  const allowlistIds = useProviderAllowlistIdSet(practiceId, practitionerId);
  const allTypes = useAppointmentSessionTypes();
  return allTypes
    .filter((t) => t.active)
    .map((t) => ({ ...t, enabledForProvider: allowlistIds.has(t.id) }));
}

export function useProviderMeetingTypeEnablement(
  practiceId: string,
  practitionerId: string,
): (MeetingType & { enabledForProvider: boolean })[] {
  const allowlistIds = useProviderAllowlistIdSet(practiceId, practitionerId);
  const allTypes = useMeetingTypes();
  return allTypes
    .filter((t) => t.active)
    .map((t) => ({ ...t, enabledForProvider: allowlistIds.has(t.id) }));
}

export function useProviderTimeEntryTypeEnablement(
  practiceId: string,
  practitionerId: string,
): (TimeEntryType & { enabledForProvider: boolean })[] {
  const allowlistIds = useProviderAllowlistIdSet(practiceId, practitionerId);
  const allTypes = useTimeEntryTypes();
  return allTypes
    .filter((t) => t.active)
    .map((t) => ({ ...t, enabledForProvider: allowlistIds.has(t.id) }));
}
